import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { ProductSpecification } from 'situview-webapi-client';
import { Guid } from 'guid-typescript';

@Component({
  selector: 'product-specification-edit',
  templateUrl: './product-specification-edit.component.html',
  styleUrls: ['./product-specification-edit.component.less']
})
export class ProductSpecificationEditComponent implements OnChanges {
  @Input() productSpecifications: ProductSpecification[] | undefined = undefined;
  @Input() productTypeIdFilter: string | undefined = undefined;
  @Input() parentProductSpecificationNode: ProductSpecificationNode | undefined = undefined;
  @Input() editState: ProductSpecificationEditState | undefined = undefined;
  @Output() onSaveUpdate: EventEmitter<ProductSpecification> = new EventEmitter();
  @Output() onSaveAdd: EventEmitter<ProductSpecification> = new EventEmitter();
  @Output() editingChange: EventEmitter<boolean> = new EventEmitter();

  createdProductSpecificationNodes: ProductSpecificationNode[] | undefined = undefined;

  ngOnChanges(changes: SimpleChanges) {
    if (this.parentProductSpecificationNode) {
      this.createdProductSpecificationNodes = this.parentProductSpecificationNode.children;
    }
    else {
      // The top level editor owns the lock that its child editors share
      if (!this.editState) {
        this.editState = new ProductSpecificationEditState(this.editingChange);
      }

      if (changes.productTypeIdFilter) {
        this.editState.expandedIds.clear();
      }

      if (this.productSpecifications) {
        if (this.productTypeIdFilter) {
          this.createdProductSpecificationNodes = ProductSpecificationNode.Build(this.productSpecifications, this.productTypeIdFilter, this.onSaveUpdate);
        } else {
          this.createdProductSpecificationNodes = [];
        }

        // Rebuilt nodes are never in edit or add mode
        this.editState.reset();
      }
    }
  }

  addNewNode() {
    var newProductSpecification = new ProductSpecification();
    newProductSpecification.id = Guid.create().toString();
    newProductSpecification.name = "New Product Specification";
    newProductSpecification.productTypeId = this.productTypeIdFilter;
    newProductSpecification.parentSpecificationId = this.parentProductSpecificationNode?.productSpecification.id;
    newProductSpecification.order = 0;
    newProductSpecification.selectable = true;

    var newSpecificationNode = new ProductSpecificationNode(newProductSpecification, undefined, this.productSpecifications!, this.onSaveUpdate);
    newSpecificationNode.addMode = true;
    this.editState?.setExpanded(newSpecificationNode, true);

    if (!this.createdProductSpecificationNodes) {
      this.createdProductSpecificationNodes = [newSpecificationNode];
    } else {
      this.createdProductSpecificationNodes?.push(newSpecificationNode);
    }

    this.editState?.begin(newSpecificationNode);
  }

  saveAdd(node: ProductSpecificationNode) {
    this.onSaveAdd.emit(node.productSpecification)
  }

  cancelAddMode(node: ProductSpecificationNode) {
    var remainingNodes = this.createdProductSpecificationNodes?.filter(e => e != node);
    this.createdProductSpecificationNodes = remainingNodes;

    // A child editor's list is its parent node's children, so keep them in step
    if (this.parentProductSpecificationNode) {
      this.parentProductSpecificationNode.children = remainingNodes;
    }

    this.editState?.setExpanded(node, false);
    this.editState?.end();
  }

  enableUpdateMode(node: ProductSpecificationNode) {
    node.enableUpdateMode();
    this.editState?.begin(node);
  }

  cancelUpdateMode(node: ProductSpecificationNode) {
    node.cancelUpdateMode();
    this.editState?.end();
  }

}

// Shared by a top level editor and its child editors: the one specification that is new or being edited,
// and which specifications are expanded so they reopen after a save rebuilds the nodes
export class ProductSpecificationEditState {
  activeNode?: ProductSpecificationNode;
  expandedIds = new Set<string>();

  constructor(private editingChange: EventEmitter<boolean>) {
  }

  get isEditing(): boolean {
    return !!this.activeNode;
  }

  begin(node: ProductSpecificationNode) {
    this.activeNode = node;
    this.editingChange.emit(true);
  }

  end() {
    this.activeNode = undefined;
    this.editingChange.emit(false);
  }

  reset() {
    this.activeNode = undefined;
  }

  isExpanded(node: ProductSpecificationNode): boolean {
    return this.expandedIds.has(node.productSpecification.id!);
  }

  setExpanded(node: ProductSpecificationNode, expanded: boolean) {
    if (expanded) {
      this.expandedIds.add(node.productSpecification.id!);
    } else {
      this.expandedIds.delete(node.productSpecification.id!);
    }
  }
}

class ProductSpecificationNode {
  productSpecification: ProductSpecification;
  productSpecificationOrig: ProductSpecification;

  children?: ProductSpecificationNode[];
  parent?: ProductSpecificationNode;

  updateMode: boolean = false;
  addMode: boolean = false;

  onSaveUpdate: EventEmitter<ProductSpecification>;

  constructor(productSpecification: ProductSpecification,
    parentProductSpecification: ProductSpecificationNode | undefined,
    productSpecifications: ProductSpecification[],
    onSaveUpdate: EventEmitter<ProductSpecification>) {
    this.productSpecificationOrig = productSpecification;
    this.productSpecification = new ProductSpecification(this.productSpecificationOrig);

    this.children = productSpecifications
      .filter(item => item.parentSpecificationId === productSpecification.id)
      .sort((a, b) => (a.order ?? 100000) - (b.order ?? 100000))
      .map(item => new ProductSpecificationNode(item, this, productSpecifications, onSaveUpdate));

    this.parent = parentProductSpecification;

    this.onSaveUpdate = onSaveUpdate;
  }

  static Build(productSpecifications: ProductSpecification[], productTypeIdFilter: string,
    onSaveUpdate: EventEmitter<ProductSpecification>) : ProductSpecificationNode[]
  {
    if (productSpecifications && productTypeIdFilter)
    {
      return productSpecifications
      .filter(item => item.productTypeId == productTypeIdFilter && !item.parentSpecificationId)
      .sort((a, b) => (a.order ?? 100000) - (b.order ?? 100000))
      .map(item => new ProductSpecificationNode(item, undefined, productSpecifications, onSaveUpdate));
    }
    else
    {
      return [];
    }
  }

  hasChildren() {
    return !!this.children && this.children.length > 0;
  }

  enableUpdateMode() {
    this.updateMode = true;
  }

  // Stays in update mode until the saved specifications are reloaded, so a failed save keeps the edits
  saveUpdate() {
    this.onSaveUpdate.emit(this.productSpecification)
  }

  cancelUpdateMode() {
    this.productSpecification = new ProductSpecification(this.productSpecificationOrig);
    this.updateMode = false;
  }
}
